/**
 * InvestorOnlyGate — INVEST-INVESTOR-GRAYWALL v0.01 (2026-05-29)
 *
 * Wraps an investor-only INVEST page. Reads profiles.subscription_tier and:
 *   - Free      → navigate to /pricing#upgrade-for-invest
 *   - Pro       → render GrayWallUpgradeCard (browse the feature names, can't run it)
 *   - Agency    → render children normally
 *   - Enterprise → render children normally
 *
 * Page CHROME (header, sidebar) is preserved regardless — the wall replaces
 * only the main content area, so the user can navigate freely between
 * locked + unlocked pages.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { GrayWallUpgradeCard } from "./GrayWallUpgradeCard";
import { getMyTier, isSuperadmin } from "@/lib/userTier";

type Tier = "free" | "pro" | "agency" | "enterprise" | "unknown";

interface Props {
  featureName: string;
  description: string;
  bullets?: string[];
  children: React.ReactNode;
}

function normalizeTier(raw: string | null | undefined): Tier {
  const s = String(raw || "").toLowerCase().trim();
  if (!s) return "unknown";
  if (s === "free" || s === "explorer") return "free";
  if (s === "pro" || s === "starter") return "pro";
  if (s === "agency" || s === "team") return "agency";
  if (s === "enterprise" || s === "scale") return "enterprise";
  return "unknown";
}

export function InvestorOnlyGate({ featureName, description, bullets, children }: Props) {
  const [tier, setTier] = useState<Tier | null>(null);
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setTier("unknown"); return; }
      // One column. The three-way fallback this replaced answered from whichever
      // of profiles.subscription_tier / plan / tier happened to be set.
      setTier(normalizeTier(await isSuperadmin() ? "enterprise" : await getMyTier()));
    })();
  }, []);

  if (tier == null) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  // cc_tasks row 82: free no longer bounces to /pricing; it gets the same card
  // (upgrade moment + reload on landing) as Pro.
  if (tier === "free" || tier === "pro" || tier === "unknown") {
    return <GrayWallUpgradeCard featureName={featureName} description={description} bullets={bullets} />;
  }
  // Agency + Enterprise — render the live page.
  return <>{children}</>;
}
